
import { Component, EventEmitter, Input, OnDestroy, Output, TemplateRef, ViewChild } from '@angular/core';

import { catchError, filter, map, take } from 'rxjs/operators';
import { MenuItem, MessageService } from 'primeng/api';
import { BehaviorSubject, combineLatest, Observable, of, Subscription, forkJoin } from 'rxjs';

import { UsersApi } from '../../../../api/users/users.api';

import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';

import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { AccountSummaryModel } from '../../../../Core/Users/models/account-summary.model';
import { UserModel } from '../../../../Core/Users/models/user.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';

import { AccountManagementService } from '../../services/account-management.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ConfirmDialogModel } from '../../../../Common/Dialogs/models/confirm-dialog.model';
import { MfaService } from '../../../../Bootstrap/services/mfa.service';
import { MfaModel, MfaTypesEnum } from '../../../../Core/Users/models/mfa.model';
import { MediaDatabaseApiUserAccount } from '../../models/media.database.api.user.account.model';
import { UserPreferencesService } from '../../../../Account/UserPreferences/services/user-preferences.service';
import { copy } from '../../../../Common/Misc/helpers/copy/copy.helper';

@Component({
    selector: 'agpr-user-profile',
    templateUrl: './user-profile.component.html',
    styleUrls: ['./user-profile.component.scss'],
    standalone: false
})
export class UserProfileComponent implements OnDestroy {
    @Input() public set user(user: UserModel) {
        this.mediaDatabaseUserAccounts = [];
        this.user_ = user;
        // need to set it to null here, because switching between users,
        // the maxSessions input is visible until the get accounts api is returned
        this.primaryAccount = null;

        this.fetchAccounts();
        this.justSentPassReset = false;
        this.justSentResendRegistration = false;
        this.updateActionButtons();
    }

    @Output() public active: EventEmitter<boolean> = new EventEmitter();
    @Output() public ghostIntoAccount: EventEmitter<number> = new EventEmitter();

    @ViewChild('actionButton') actionButton: TemplateRef<any>;

    user_: UserModel;

    internalPermission: boolean = false;
    ghostInPermission: boolean = false;

    justSentPassReset: boolean = false;
    justSentResendRegistration: boolean = false;
    subscriptions: Subscription[] = [];

    isReadOnly: boolean = false;
    isSsoUser: boolean = false;

    permissions: userPermissionsEnum[];
    loading: boolean = false;

    accounts: AccountSummaryModel[] = [];
    selectedAccount: AccountSummaryModel;
    primaryAccount: AccountSummaryModel;
    mediaDatabaseUserAccounts: MediaDatabaseApiUserAccount[] = [];
    accountsPartnerResellerId: number; // If all accounts have the same partnerResellerId, otherwise null
    mfaMethods: MfaModel[] = [];
    preferredLanguageOptions: DropdownOptionModel[] = [];
    hasMediaMonitoringApi: boolean = false;

    actionButtons: MenuItem[] = [];

    readonly messageSeverityEnum = messageSeverityEnum;
    readonly userPermissions = userPermissionsEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly internalUserPermissions: Set<userPermissionsEnum> = new Set([
        userPermissionsEnum.accountGhostIn,
        userPermissionsEnum.accountManager,
        userPermissionsEnum.accountsReadOnlyManager,
        userPermissionsEnum.advancedMonitoringManager,
        // TODO: Uncomment when NEP-30354 is done: Advanced Monitoring Manager permission is split into
        // Topic Archive Backfills and Mention Renormalization (Bulk Action)
        // userPermissionsEnum.topicArchiveBackFills,
        // userPermissionsEnum.mentionRenormalization,
        userPermissionsEnum.advancedMonitoringFullText,
        userPermissionsEnum.socialSnapshot,
        userPermissionsEnum.copyMentionsToAccount,
        userPermissionsEnum.snapshotPushToMercury,
        userPermissionsEnum.wireEditorial,
        userPermissionsEnum.journodexManager,
        userPermissionsEnum.csvImportToMercury,
        userPermissionsEnum.exposedMentionLicenseInfo,
    ]);
    readonly allUserPermissions = [
        {
            enum: userPermissionsEnum.accountGhostIn,
            displayString: 'Ghost-In',
            isInternalUserPermission: this.internalUserPermissions.has(userPermissionsEnum.accountGhostIn)
        },
        {
            enum: userPermissionsEnum.accountManager,
            displayString: 'Accounts Manager',
            isInternalUserPermission: this.internalUserPermissions.has(userPermissionsEnum.accountManager)
        },
        {
            enum: userPermissionsEnum.accountsReadOnlyManager,
            displayString: 'Account Read-Only Manager',
            isInternalUserPermission: this.internalUserPermissions.has(userPermissionsEnum.accountsReadOnlyManager)
        },
        {
            enum: userPermissionsEnum.advancedMonitoringManager,
            displayString: 'Advanced Monitoring Manager',
            tooltip: 'Gives access to Renormalize function and Backfill Requests.',
            isInternalUserPermission: this.internalUserPermissions.has(userPermissionsEnum.advancedMonitoringManager)
        },
        // TODO: Uncomment when NEP-30354 is done: Advanced Monitoring Manager permission is split into
        // Topic Archive Backfills and Mention Renormalization (Bulk Action)
        // {
        //     enum: userPermissionsEnum.topicArchiveBackFills,
        //     displayString: 'Topic Archive Backfills',
        //     isInternalUserPermission: this.internalUserPermissions.has(userPermissionsEnum.topicArchiveBackFills)
        // },
        // {
        //     enum: userPermissionsEnum.mentionRenormalization,
        //     displayString: 'Mention Renormalization (Bulk Action)',
        //     isInternalUserPermission: this.internalUserPermissions.has(userPermissionsEnum.mentionRenormalization)
        // },
        {
            enum: userPermissionsEnum.apiKeyManagement,
            displayString: 'API Key Manager',
            tooltip: 'Permission available when associated with accounts that have Media Monitoring API access.',
            isInternalUserPermission: this.internalUserPermissions.has(userPermissionsEnum.apiKeyManagement),
        },
        {
            enum: userPermissionsEnum.advancedMonitoringFullText,
            displayString: 'Full-Text Mentions',
            isInternalUserPermission: this.internalUserPermissions.has(userPermissionsEnum.advancedMonitoringFullText)
        },
        {
            enum: userPermissionsEnum.copyMentionsToAccount,
            displayString: 'Copy to Account (Bulk Action)',
            tooltip: 'Note: Must also enable either the "Account Manager" or "Account Readonly Manager" permission.',
            isInternalUserPermission: this.internalUserPermissions.has(userPermissionsEnum.copyMentionsToAccount)
        },
        {
            enum: userPermissionsEnum.socialSnapshot,
            displayString: 'Social Snapshot',
            isInternalUserPermission: this.internalUserPermissions.has(userPermissionsEnum.socialSnapshot)
        },
        {
            enum: userPermissionsEnum.snapshotPushToMercury,
            displayString: 'Snapshot Push To Mercury',
            tooltip: 'Note: Must also enable Social Snapshot permission.',
            isInternalUserPermission: this.internalUserPermissions.has(userPermissionsEnum.snapshotPushToMercury)
        },
        {
            enum: userPermissionsEnum.csvImportToMercury,
            displayString: 'Import Mentions to System Collection',
            isInternalUserPermission: this.internalUserPermissions.has(userPermissionsEnum.csvImportToMercury)
        },
        {
            enum: userPermissionsEnum.exposedMentionLicenseInfo,
            displayString: 'Exposed Mention License Information',
            isInternalUserPermission: this.internalUserPermissions.has(userPermissionsEnum.exposedMentionLicenseInfo)
        },
        {
            enum: userPermissionsEnum.wireEditorial,
            displayString: 'Newswire Editor',
            isInternalUserPermission: this.internalUserPermissions.has(userPermissionsEnum.wireEditorial)
        },
        {
            enum: userPermissionsEnum.journodexManager,
            displayString: 'Journodex Manager',
            tooltip: 'Note: Grants no power if the user is not part of the research account.',
            isInternalUserPermission: this.internalUserPermissions.has(userPermissionsEnum.journodexManager)
        }
    ];

    readonly maxSessionOptions = [
        new DropdownOptionModel('1', 1),
        new DropdownOptionModel('2', 2),
        new DropdownOptionModel('3', 3),
        new DropdownOptionModel('4', 4),
        new DropdownOptionModel('5', 5),
        new DropdownOptionModel('6', 6),
        new DropdownOptionModel('7', 7),
        new DropdownOptionModel('8', 8),
        new DropdownOptionModel('9', 9),
        new DropdownOptionModel('10', 10),
        new DropdownOptionModel('UMT.profile.notLimited', null),
    ];

    constructor(
        public usersApi: UsersApi,
        public messageService: MessageService,
        public accountManagementService: AccountManagementService,
        private localizationService: LocalizationService,
        public userStateService: CurrentUserStateService,
        private coreDialogService: CoreDialogService,
        private mfaService: MfaService,
        private userPreferencesService: UserPreferencesService
    ) {
        this.subscriptions.push(
            this.accountManagementService.getReadOnly().subscribe(result => {
                this.isReadOnly = result;
                this.updateActionButtons();
            }),
            this.accountManagementService.loading$.subscribe(x => {
                this.loading = x;
                this.updateActionButtons();
            }),
            this.userStateService.getUser().pipe(filter(x => !!x)).subscribe(user => {
                this.internalPermission = user.internal;
                this.ghostInPermission = user.permissions.includes(userPermissionsEnum.accountGhostIn) && user.neptuneOnly;
            }),
            this.mfaService.all$.subscribe(all => this.mfaMethods = all),
            this.userPreferencesService.getReferenceDataLanguages().subscribe(
                languages => {
                    this.preferredLanguageOptions = (languages ?? [])
                        .map(x => new DropdownOptionModel(this.localizationService.transform(`Language.${x.languageTag}`), x.id))
                        .sort((a, b) => a.label.localeCompare(b.label));
                }
            ),
            this.accountManagementService.refreshMediaDBAccount$.subscribe(x => {
                if(x) {
                    this.setupMediaDatabaseApiAccounts();
                }
            })
        );
        if (this.user) {
            this.accountLookup(this.user.accountsIds).subscribe(accounts => {
                this.accounts = accounts;
                this.setPrimaryAccount();
                this.setAccountsResellerId();
                this.updateHasMediaMonitoringApi();
            });
        }
        this.mfaService.refreshAllMfaSetting();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(x => x.unsubscribe());
    }

    fetchAccounts() {
        this.usersApi.getAccountBatch(this.user_.accountsIds).subscribe(apiAccounts => {
            this.accounts = apiAccounts.map(account => new AccountSummaryModel(account));
            this.setPrimaryAccount();
            this.setAccountsResellerId();
            this.isSsoUser = !!apiAccounts.find(apiAccount => apiAccount.id === this.primaryAccount?.id)?.ssoProviderId;
            this.setupMediaDatabaseApiAccounts();
            this.updateActionButtons();
            this.updateHasMediaMonitoringApi();
        });
    }

    private updateHasMediaMonitoringApi() {
        // API Key Management permission will only be available for users that are associated with accounts that have Media Monitoring API access
        this.hasMediaMonitoringApi = this.accounts?.some(account =>
            account.permissions?.includes(userPermissionsEnum.mediaMonitoringApi)
        );
        if (!this.hasMediaMonitoringApi) {
            this.user_.permissions = this.user_.permissions?.filter(p => p !== userPermissionsEnum.apiKeyManagement);
        }
    }

    // Sets resellerId if all of the user's accounts have the same non-null resellerId; otherwise, sets it to null.
    private setAccountsResellerId(): void {
        const firstResellerId = this.accounts?.[0]?.partnerResellerId;
        if (!firstResellerId && firstResellerId !== 0) {
            this.accountsPartnerResellerId = null;
            return;
        }

        const isAllSame = this.accounts.every(account => account.partnerResellerId === firstResellerId);
        this.accountsPartnerResellerId = isAllSame ? firstResellerId : null;

        this.updateUserPermissions();
    }

    // Updates user permissions:
    // Remove internal permissions for client users
    // For Ghost-In permission, it is available if all accounts are of the same partner reseller.
    private updateUserPermissions(): void {
        if (!this.user_.internal && this.user_?.permissions?.length) {
            this.user_.permissions = this.user_.permissions.filter(
                permission => this.internalUserPermissions.has(permission)
                    ? (permission === userPermissionsEnum.accountGhostIn ? !!this.accountsPartnerResellerId : false)
                    : true
            );
        }
    }

    // html is using this
    accountLookup(ids: number[]): Observable<AccountSummaryModel[]> {
        return this.usersApi.getAccountBatch(ids).pipe(map(x => {
            return x.map(account => new AccountSummaryModel(account));
        }));
    }

    toggleUserActivity() {
        if (!this.user_.isActive) {
            this.subscriptions.push(
                this.usersApi.activateUser(this.user_.id).subscribe({
                    next: () => {
                        this.messageService.add({
                            severity: messageSeverityEnum.success,
                            summary: this.user_.firstName + ' ' + this.user_.lastName + ' activated.'
                        });
                        this.user_.isActive = !this.user_.isActive;
                        this.active.emit(true);
                    },
                    error: () => {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: this.localizationService.transform('UMT.activateUser.error')
                        });
                    }
                }));
        } else if (this.accounts.length > 1) {
            const confirmConfig: ConfirmDialogModel = {
                inputs: {
                    id: 'userProfile.deactivateUserConfirmDialog',
                    heading: 'UMT.header.deactivateUser',
                    okLabel: 'UMT.header.deactivateUser.confirm',
                    messageHtml: this.localizationService.transform('UMT.deactivateUser.confirm.messageHtml', { name: this.userFullName }),
                    list: this.accounts.map(account => account.name),
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {
                            this.executeDeactivateUser();
                        }
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
        } else {
            this.executeDeactivateUser();
        }
    }

    private get userFullName(): string {
        return `${this.user_.firstName} ${this.user_.lastName}`;
    }

    private executeDeactivateUser() {
        this.subscriptions.push(
            this.usersApi.deactivateUser(this.user_.id).subscribe({
                next: () => {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: this.localizationService.transform('UMT.deactivateUser.success.summary'),
                        detail: this.localizationService.transform('UMT.deactivateUser.success.detail', { name: this.userFullName })
                    });
                    this.user_.isActive = false;
                    this.active.emit(false);
                    if (this.user_.permissions?.includes(userPermissionsEnum.apiKeyManagement)) {
                        this.checkLastApiKeyManagerAndWarn();
                    }
                },
                error: () => {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('UMT.deactivateUser.error')
                    });
                }
            }));
    }

    userTypeChange(): void {
        this.updateUserPermissions();
    }

    sendPasswordReset() {
        this.subscriptions.push(
            this.usersApi.sendPasswordReset(this.user_.username).subscribe(done => {
                this.justSentPassReset = true;
                this.updateActionButtons();
                this.messageService.add(
                    {
                        severity : messageSeverityEnum.success,
                        summary : 'Password reset sent to ' + this.user_.email
                    });
            }));
    }

    resendRegistrationEmail() {
        this.usersApi.sendRegistrationEmail(this.user_.username)
            .pipe(take(1))
            .subscribe({
                next: () => {
                    this.justSentResendRegistration = true;
                    this.updateActionButtons();
                    this.messageService.add({
                        severity : messageSeverityEnum.success,
                        summary : this.localizationService.transform(
                            'UMT.resendRegistration.success',
                            { username: this.user_.email }
                        )
                    });
                },
                error: () => {
                    this.messageService.add({
                        severity : messageSeverityEnum.success,
                        summary : 'UMT.resendRegistration.error'
                    });
                }
            });
    }

    addAccount(item: AccountSummaryModel) {
        if (!this.accounts.some(x => x.id === item.id)) {
            this.accounts.push(item);
        }
        this.user_.accountsIds = this.accounts.map(account => account.id);
        this.setPrimaryAccount();

        if (item.permissions?.includes(userPermissionsEnum.mediaDatabaseApi)) {
            this.setupMediaDatabaseApiAccounts();
        }
        this.setAccountsResellerId();
        this.updateHasMediaMonitoringApi();
    }

    removeAccount(accountId: number) {
        let index = this.accounts.findIndex(x => x.id === accountId);
        if (index >= 0) {
            this.accounts.splice(index, 1);
        }

        index = this.user_.accountsIds.findIndex(x => x === accountId);
        if (index >= 0) {
            this.user_.accountsIds.splice(index, 1);
        }
        this.setPrimaryAccount();

        this.deleteMediaDatabaseAccount(accountId);
        this.setAccountsResellerId();
        this.updateHasMediaMonitoringApi();
    }

    makePrimary(account: AccountSummaryModel) {
        this.user_.primaryAccountId = account.id;
        this.setPrimaryAccount();
    }

    setPrimaryAccount() {
        if (this.accounts && this.user_) {
            this.primaryAccount = this.accounts.find(x => x.id === this.user_.primaryAccountId);
            this.accountManagementService.setSelectedUserPrimaryAccount(this.primaryAccount);
        } else {
            this.accountManagementService.setSelectedUserPrimaryAccount(null);
        }
    }

    onPermissionChanged(permission: userPermissionsEnum, checked: boolean): void {
        if (permission !== userPermissionsEnum.apiKeyManagement || checked) {
            return;
        }
        this.checkLastApiKeyManagerAndWarn();
    }

    private checkLastApiKeyManagerAndWarn(): void {
        const miApiAccounts = this.accounts.filter(account =>
            account.permissions?.includes(userPermissionsEnum.mediaMonitoringApi)
        );

        if (!miApiAccounts.length) {
            return;
        }

        forkJoin(
            miApiAccounts.map(account =>
                this.usersApi.getAllUsersFromAccount(account.id, userPermissionsEnum.apiKeyManagement).pipe(
                    map(users => ({
                        account,
                        hasOtherActiveManager: users.some(u => u.isActive && u.id !== this.user_.id)
                    }))
                )
            )
        ).subscribe(results => {
            const affectedAccounts = results
                .filter(r => !r.hasOtherActiveManager)
                .map(r => r.account.name);

            if (!affectedAccounts.length) {
                return;
            }

            const confirmConfig: ConfirmDialogModel = {
                inputs: {
                    id: 'userProfile.apiKeyManagerWarningDialog',
                    heading: 'UMT.apiKeyManager.warning.heading',
                    alertType: messageSeverityEnum.warning,
                    message: 'UMT.apiKeyManager.warning.message',
                    list: affectedAccounts,
                    okLabel: 'UMT.apiKeyManager.warning.acknowledge',
                    showCancel: false,
                    closeOnClickOutside: false,
                },
                outputs: {
                    dialogClose: () => {}
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
        });
    }

    ghostIn(accountId: number) {
        this.ghostIntoAccount.emit(accountId);
    }

    resetMfa() {
        const confirmConfig: ConfirmDialogModel = {
            inputs: {
                id: 'userProfile.resetMfaConfirmDialog',
                heading: 'Mfa.umt.reset',
                alertType: messageSeverityEnum.warning,
                okLabel: 'Mfa.reset',
                alertMessage: 'Mfa.umt.reset.warningMessage',
                message: 'Mfa.umt.reset.message',
                list: this.mfaMethods.map(x =>
                    x.name = this.localizationService.transform(`Mfa.method.${x.type}`,
                        {name: x.type === MfaTypesEnum.email ? x.email : x.name})
                ),
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.usersApi.resetMfa(this.user_.id).subscribe({
                            next: () => {
                                this.messageService.add({
                                    severity: messageSeverityEnum.success,
                                    summary: 'Mfa.reset.success',
                                });
                                this.mfaService.refreshAllMfaSetting();
                            },
                            error: () => {
                                this.messageService.add({
                                    severity: messageSeverityEnum.error,
                                    summary: 'Mfa.reset.error',
                                });
                            }
                        });
                    }
                }
            }
        };

        this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
    }

    setupMediaDatabaseApiAccounts(): void {
        const accountIds = [];
        this.accounts.forEach(account => {
            if (account.permissions?.includes(userPermissionsEnum.mediaDatabaseApi)) {
                accountIds.push(account.id);
            }
        });

        this.getMediaDatabaseApiAccounts(accountIds);
    }

    getMediaDatabaseApiAccounts(accountIds: number[]): void {
        const apiCalls: Observable<any[]>[] = [];

        accountIds?.forEach(accountId => {
            if (!this.mediaDatabaseUserAccounts.some(x => x.accountId === accountId)) {
                const apiCall = combineLatest([
                    this.accountManagementService.getAccountById(accountId),
                    this.accountManagementService.getUserApiKey(accountId, this.user_.id).pipe(
                        catchError(error => {
                            if (error.error?.text) {
                                return of(error.error?.text);
                            }
                            return of('');
                        })
                    ),
                    this.accountManagementService.getUserAccountPreferences(accountId, this.user_.id).pipe(catchError(err => of(null)))
                ]);

                apiCalls.push(apiCall);
            }
        });

        if (apiCalls?.length) {
            combineLatest(apiCalls).subscribe((res) => {
                res?.forEach(([account, apiKey, updatePreferences]) => {
                    const mediaDatabaseApiUserAccount = new MediaDatabaseApiUserAccount();
                    mediaDatabaseApiUserAccount.accountName = account.name;
                    mediaDatabaseApiUserAccount.accountId = account.id;
                    mediaDatabaseApiUserAccount.partnerReseller = account.mediaApiPartnerResellerName ? account.mediaApiPartnerResellerName : 'None';
                    mediaDatabaseApiUserAccount.apiKey = apiKey;
                    mediaDatabaseApiUserAccount.accessToken = account.accessTokens?.length ? account.accessTokens[0] : null;
                    mediaDatabaseApiUserAccount.clientId = account.clientId;
                    mediaDatabaseApiUserAccount.userClientId = this.user_.userClientId;
                    mediaDatabaseApiUserAccount.updatePreferences = updatePreferences;
                    if(updatePreferences) {
                        mediaDatabaseApiUserAccount.selectedPreferredLanguage = this.preferredLanguageOptions
                            .find(option => option.value === updatePreferences.referenceDataLanguageId);
                    }

                    this.mediaDatabaseUserAccounts.push(mediaDatabaseApiUserAccount);

                    this.user_.mediaDatabaseUserAccounts = this.mediaDatabaseUserAccounts;
                });
            });
        }
    }

    deleteMediaDatabaseAccount(accountId: number): void {
        const index = this.mediaDatabaseUserAccounts.findIndex(x => x.accountId === accountId);
        if (index !== -1) {
            this.mediaDatabaseUserAccounts.splice(index, 1);
        }
    }

    preferredLanguageChanged(mediaDatabaseUserAccount: MediaDatabaseApiUserAccount, languageId: number): void {
        mediaDatabaseUserAccount.updatePreferences.referenceDataLanguageId = languageId;
        this.accountManagementService.updateUserAccountPreferences(
            mediaDatabaseUserAccount.accountId, this.user_.id, mediaDatabaseUserAccount.updatePreferences
        ).subscribe(() => {
            this.messageService.add(
                {
                    severity : messageSeverityEnum.success,
                    summary: this.localizationService.transform('UMT.profile.MediaDatabaseApi.preferredLanguage.summary'),
                    detail: this.localizationService.transform(
                        'UMT.profile.MediaDatabaseApi.preferredLanguage.message', {name: mediaDatabaseUserAccount.accountName}
                    )
                });
        });
    }

    copy(content: string): void {
        copy.text(content);

        this.messageService.add({
            severity: messageSeverityEnum.success,
            summary: this.localizationService.transform('AMT.accountSettings.mediaDatabaseApis.copy.summary'),
            detail: this.localizationService.transform('AMT.accountSettings.mediaDatabaseApis.copy.message'),
        });
    }

    updateActionButtons(): void {
        this.actionButtons = [
            {
                label: 'UMT.header.deactivateUser',
                disabled: this.loading
                    || this.isReadOnly
                    || !this.user_?.neptuneOnly,
                command: () => this.toggleUserActivity()
            },
            {
                label: 'UMT.header.passwordReset',
                disabled: this.loading
                    || this.isReadOnly
                    || this.isSsoUser
                    || !this.user_?.neptuneOnly
                    || !this.user_?.username
                    || this.justSentPassReset,
                command: () => this.sendPasswordReset()
            },
            {
                label: 'UMT.header.resendRegistration',
                disabled: this.loading
                    || this.isReadOnly
                    || this.isSsoUser
                    || !this.user_?.neptuneOnly
                    || !this.user_?.email
                    || this.justSentResendRegistration,
                command: () => this.resendRegistrationEmail()
            }
        ];
    }
}
